import React from "react";
import { Box, Typography } from "@material-ui/core";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import useCommonStyles from "./style";

// Parameters + option sets taken from the Health Info PDF (Options / Values column).
// `overview: true` marks params that are also surfaced on the Overview tab.
const PARAM_ICONS = {
  akrti: "🧍",
  agni: "🔥",
  ama: "🤢",
  ojas: "✨",
  nidana: "🧬",
  koshtha: "🚽",
  satmya: "🛡️",
  sattva: "🧠",
  mala: "🧫",
  sparsha: "✋",
  mutra: "💧",
  indriya: "👁️",
};

// Each module's dropdown options are the canonical "Output" classifications it
// can return (Assessment Modules output spec). `moduleId` = its id in the
// assessment session API; `field` = which classification key holds the value
// (type | label | pattern); each option carries the stable enum `id` so a
// doctor's pick can be saved back as { id, [field]: label }.
export const ASSESSMENT_PARAMS = [
  {
    key: "akrti",
    moduleId: "akriti",
    field: "label",
    label: "Ākṛti (Body & Face Signs)",
    options: [
      { id: "CLEAR", label: "Nirvikāra Ākṛti" },
      { id: "MILD", label: "Alpa Vikṛti" },
      { id: "MODERATE", label: "Madhya Vikṛti" },
      { id: "SIGNIFICANT", label: "Guru Vikṛti" },
    ],
  },
  {
    key: "agni",
    moduleId: "agni",
    field: "type",
    label: "Agni (Digestive Fire)",
    overview: true,
    options: [
      { id: "SAMA", label: "Sama Agni" },
      { id: "MANDA", label: "Manda Agni" },
      { id: "TIKSHNA_MILD", label: "Tikshna Agni (mild)" },
      { id: "TIKSHNA_SEVERE", label: "Tikshna Agni (moderate–severe)" },
      { id: "VISHAMA", label: "Vishama Agni" },
    ],
  },
  {
    key: "ama",
    moduleId: "ama",
    field: "label",
    label: "Āma (Toxin Load)",
    overview: true,
    options: [
      { id: "NIRAMA", label: "Nirāma / Agni Sama" },
      { id: "ALPA", label: "Alpa Āma" },
      { id: "MADHYA", label: "Madhya Āma" },
      { id: "GURU", label: "Guru Āma" },
    ],
  },
  {
    key: "ojas",
    moduleId: "ojas",
    field: "pattern",
    label: "Ojas (Vital Essence)",
    overview: true,
    options: [
      { id: "KSHAYA", label: "Kṣaya — Critical" },
      { id: "KSHAYA", label: "Kṣaya — Severe" },
      { id: "KSHAYA", label: "Kṣaya — Moderate" },
      { id: "VYAPAT_KSHAYA", label: "Vyāpat–Kṣaya (Transitional)" },
      { id: "VYAPAT", label: "Vyāpat — Moderate-Severe" },
      { id: "VYAPAT", label: "Vyāpat — Mild" },
      { id: "VISRAMSA", label: "Viśraṁsa" },
      { id: "SUBCLINICAL", label: "Subclinical" },
      { id: "SAMYA", label: "Ojas Sāmya (Healthy)" },
    ],
  },
  {
    key: "nidana",
    moduleId: "nidana",
    field: "pattern",
    label: "Nidāna (Causative Factors)",
    overview: true,
    options: [
      { id: "NIDANA_MINIMAL", label: "Minimal Nidāna Burden" },
      { id: "VATA_DOMINANT", label: "Vāta Nidāna Predominant" },
      { id: "PITTA_DOMINANT", label: "Pitta Nidāna Predominant" },
      { id: "KAPHA_DOMINANT", label: "Kapha Nidāna Predominant" },
      { id: "VATA_PITTA", label: "Vāta-Pitta Combined" },
      { id: "VATA_KAPHA", label: "Vāta-Kapha Combined" },
      { id: "PITTA_KAPHA", label: "Pitta-Kapha Combined" },
      { id: "TRIDOSHA", label: "Tridosha Burden" },
    ],
  },
  {
    key: "koshtha",
    moduleId: "kostha",
    field: "type",
    label: "Koṣṭha (Bowel Nature)",
    options: [
      { id: "MADHYAMA", label: "Madhyama Kostha" },
      { id: "MRIDU_STRONG", label: "Mṛdu Kostha (marked)" },
      { id: "MRIDU_MODERATE", label: "Mṛdu Kostha (mild–moderate)" },
      { id: "KRURA_STRONG", label: "Krūra Kostha (marked)" },
      { id: "KRURA_MODERATE", label: "Krūra Kostha (mild–moderate)" },
      { id: "VARIABLE", label: "Sama / Variable Kostha" },
    ],
  },
  {
    key: "satmya",
    moduleId: "satmya",
    field: "label",
    label: "Sātmya (Adaptability)",
    options: [
      { id: "PRAVARA", label: "Pravara Sātmya" },
      { id: "MADHYAMA", label: "Madhyama Sātmya" },
      { id: "AVARA", label: "Avara Sātmya" },
      { id: "HINA", label: "Hīna Sātmya" },
    ],
  },
  {
    key: "sattva",
    moduleId: "satva",
    field: "label",
    label: "Sattva (Mental Strength)",
    options: [
      { id: "UTTAMA", label: "Uttama Sattva" },
      { id: "MADHYAMA", label: "Madhyama Sattva" },
      { id: "AVARA", label: "Avara Sattva" },
      { id: "HINA", label: "Hīna Sattva" },
    ],
  },
  {
    key: "mala",
    moduleId: "stool",
    field: "type",
    label: "Māla (Stool Examination)",
    options: [
      { id: "PRAKRTA", label: "Prakṛta (Healthy)" },
      { id: "VATA", label: "Vāta Pattern" },
      { id: "PITTA", label: "Pitta Pattern" },
      { id: "KAPHA_AMA", label: "Kapha / Āma Pattern" },
      { id: "MIXED", label: "Mixed Pattern (Multi-Dosha)" },
      { id: "RED_FLAG", label: "Red Flag — Urgent Referral" },
    ],
  },
  {
    key: "sparsha",
    moduleId: "touch",
    field: "type",
    label: "Sparśa (Touch Examination)",
    options: [
      { id: "BALANCED", label: "Balanced" },
      { id: "VATA", label: "Vāta Dominance" },
      { id: "PITTA", label: "Pitta Dominance" },
      { id: "KAPHA", label: "Kapha / Āma Dominance" },
      { id: "VATA_KAPHA", label: "Vāta–Kapha Mixed" },
      { id: "RED_FLAG", label: "Red Flag — Refer for Evaluation" },
    ],
  },
  {
    key: "mutra",
    moduleId: "urine",
    field: "type",
    label: "Mutra (Urine Examination)",
    options: [
      { id: "BALANCED", label: "Balanced (Normal Mutra)" },
      { id: "VATA", label: "Vāta Pattern (Mutra Vibandha)" },
      { id: "PITTA", label: "Pitta Pattern (Mutra Dāha)" },
      { id: "KAPHA", label: "Kapha / Āma Pattern (Mutra Kapha)" },
      { id: "PRAMEHA", label: "Prameha Risk (Diabetes / Metabolic)" },
      { id: "RED_FLAG", label: "Red Flag — Urgent Medical Referral" },
    ],
  },
];

// indriya is per-organ (netra/shrotra/ghrana/jihva/tvak) — rendered separately by
// IndriyaParameters, not as a single dropdown here.

const AssessmentParameters = ({
  values = {},
  onOpenModule,
  openableModuleIds = [],
}) => {
  const classes = useCommonStyles();

  return (
    <Box className={classes.hiParamGrid}>
      {ASSESSMENT_PARAMS.map((param) => {
        const selected = values[param.key];
        return (
          <Box
            key={param.key}
            className={`${classes.hiParamRow} ${
              selected ? classes.hiParamRowActive : ""
            }`}
          >
            <span className={classes.hiParamIcon}>
              {selected ? (
                <CheckCircleRoundedIcon className={classes.hiParamCheck} />
              ) : (
                PARAM_ICONS[param.key] || "•"
              )}
            </span>
            <Typography
              className={`${classes.hiParamLabel} ${
                selected ? classes.hiParamLabelActive : ""
              } ${
                onOpenModule && openableModuleIds.includes(param.moduleId)
                  ? classes.hiParamLabelClickable
                  : ""
              }`}
              title={
                onOpenModule && openableModuleIds.includes(param.moduleId)
                  ? "View / edit questions"
                  : undefined
              }
              onClick={
                onOpenModule && openableModuleIds.includes(param.moduleId)
                  ? () => onOpenModule(param)
                  : undefined
              }
            >
              {param.label}
            </Typography>
            <Typography
              className={
                selected ? classes.hiParamValue : classes.hiParamValueEmpty
              }
            >
              {selected || "Not assessed"}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default AssessmentParameters;
